import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import PaginationControls from '../../utils/PaginationControls';
import CountryFlag from '../../utils/CountryFlag';
import authNavigate from '../../globalComponents/AuthNav';
import { useNavigate } from 'react-router-dom';

// Colour variants come from the shared .category-badge-1 … -7 classes
// (DashboardView.css), so pills follow the light/dark theme automatically.
const PILL_VARIANT_COUNT = 7;

// Hash any string (tag or country name) to one of the category-badge classes —
// used everywhere a tag or country shows as a pill (filter dropdown, active-
// filter chips, card tags and the tags popup), so the same value always gets
// the same colour no matter where it's rendered.
const getPillClass = (value) => {
  if (!value) return 'category-badge-1';
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `category-badge-${(Math.abs(hash) % PILL_VARIANT_COUNT) + 1}`;
};

// The two — and only two — buckets a project's tags can live in.
const TAG_TYPES = [
  { key: 'indication', label: 'Indication' },
  { key: 'intervention', label: 'Intervention' }
];
const EMPTY_CATEGORY_FILTER = { indication: [], intervention: [] };

// One colour per bucket, used everywhere a tag shows: the card pills, the tags
// popup, the filter dropdown and the active-filter chips. Only colours are set
// here, so each pill keeps the size of its own class. Change the hex values to
// pick different colours.
const TAG_TYPE_STYLE = {
  indication:   { backgroundColor: '#FCE7F3', color: '#BE185D', border: '1px solid #F9A8D4' }, // light pink
  intervention: { backgroundColor: '#FFEDD5', color: '#C2410C', border: '1px solid #FB923C' }  // orange
};

// ─── Filters Dropdown ─────────────────────────────────────────────────────
// Country and Tags at the top, Indication/Intervention nested inside Tags in
// its own small panel. The box ends where the first row of project cards ends
// (measured below), and only the options list scrolls.
const FiltersDropdown = ({
  countries = [],
  countryFilter = [],
  onCountryChange,
  categories = EMPTY_CATEGORY_FILTER,
  categoryFilter = EMPTY_CATEGORY_FILTER,
  onCategoryChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('country'); // 'country' | 'tags'
  const [activeTagType, setActiveTagType] = useState('indication');
  const [search, setSearch] = useState('');
  const [tempCountry, setTempCountry] = useState(countryFilter);
  const [tempCategory, setTempCategory] = useState(categoryFilter);
  const dropdownRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setTempCountry(countryFilter); }, [countryFilter]);
  useEffect(() => { setTempCategory(categoryFilter); }, [categoryFilter]);

  // Make the dropdown end where the first row of project cards ends, at any
  // screen size or zoom. Card height depends on its content, so it is measured
  // here and handed to CSS as --filters-h (App.css: .tag-multiselect-dropdown).
  // Re-measured on open, tab switch, window resize / zoom, and card resize.
  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const panel = panelRef.current;
    const card = panel?.closest('.ev-card-4')?.querySelector('.bms-fabric-card');

    const fit = () => {
      if (!panel || !card) {
        panel?.style.removeProperty('--filters-h');
        return;
      }
      const top = panel.getBoundingClientRect().top;
      const toCardEnd = card.getBoundingClientRect().bottom - top;
      const toWindowEnd = window.innerHeight - top - 16; // never past the screen
      const height = Math.min(toCardEnd, toWindowEnd);
      if (height > 0) panel.style.setProperty('--filters-h', `${height}px`);
      else panel.style.removeProperty('--filters-h');
    };

    fit();
    window.addEventListener('resize', fit);
    const observer = card && window.ResizeObserver ? new ResizeObserver(fit) : null;
    if (observer) observer.observe(card);
    return () => {
      window.removeEventListener('resize', fit);
      if (observer) observer.disconnect();
    };
  }, [isOpen, activeTab]);

  const activeCount =
    countryFilter.length + categoryFilter.indication.length + categoryFilter.intervention.length;
  const tagCount = tempCategory.indication.length + tempCategory.intervention.length;

  const isCountryTab = activeTab === 'country';
  const currentTagList = isCountryTab ? [] : (categories[activeTagType] || []);

  const toggleCountry = (id) => setTempCountry(prev =>
    prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
  );

  const toggleTag = (tag) => setTempCategory(prev => ({
    ...prev,
    [activeTagType]: prev[activeTagType].includes(tag)
      ? prev[activeTagType].filter(t => t !== tag)
      : [...prev[activeTagType], tag]
  }));

  const handleSelectAll = () => {
    if (isCountryTab) setTempCountry(countries.map(c => c.id));
    else setTempCategory(prev => ({ ...prev, [activeTagType]: [...currentTagList] }));
  };
  const handleDeselectAll = () => {
    if (isCountryTab) setTempCountry([]);
    else setTempCategory(prev => ({ ...prev, [activeTagType]: [] }));
  };

  const handleClearAll = () => {
    setTempCountry([]);
    setTempCategory(EMPTY_CATEGORY_FILTER);
    onCountryChange([]);
    onCategoryChange(EMPTY_CATEGORY_FILTER);
    setIsOpen(false);
  };

  const handleApply = () => {
    onCountryChange(tempCountry);
    onCategoryChange(tempCategory);
    setIsOpen(false);
  };

  const filteredCountries = countries.filter(c =>
    (c.name || '').toLowerCase().includes(search.toLowerCase())
  );
  const filteredTags = currentTagList.filter(t =>
    t.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="d-inline-block" ref={dropdownRef}>
      <div className="position-relative d-inline-block">
        <button
          type="button"
          className="ev-message-card2 ev-btn-xsmall ev-fs-11"
          onClick={() => setIsOpen(o => !o)}
        >
          <i className="bi bi-funnel me-1"></i>Filters
          {activeCount > 0 && <span> ({activeCount})</span>}
          <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} ms-1`}></i>
        </button>

        {isOpen && (
          // Size comes from App.css (.tag-multiselect-dropdown); no vh class here.
          <div ref={panelRef} className="tag-multiselect-dropdown position-absolute d-flex flex-column">

            {/* Top level — Country / Tags */}
            <div className="d-flex gap-1 mb-2 flex-shrink-0">
              <button
                type="button"
                className={`ev-btn-small ev-bold-reg flex-grow-1 ${isCountryTab ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                onClick={() => { setActiveTab('country'); setSearch(''); }}
              >
                <i className="bi bi-globe me-1"></i>Country
                {tempCountry.length > 0 && ` (${tempCountry.length})`}
              </button>
              <button
                type="button"
                className={`ev-btn-small ev-bold-reg flex-grow-1 ${!isCountryTab ? 'ev-btn-blue-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                onClick={() => { setActiveTab('tags'); setSearch(''); }}
              >
                <i className="bi bi-tags me-1"></i>Tags
                {tagCount > 0 && ` (${tagCount})`}
              </button>
            </div>

            {/* Second level, nested inside Tags — its own small boxed panel */}
            {!isCountryTab && (
              <div className="ev-card-2 p-2 mb-2 flex-shrink-0">
                <p className="ev-xsmall-p-blue ev-bold-med mb-1 ms-1">Category</p>
                <div className="d-flex gap-1">
                  {TAG_TYPES.map(({ key, label }) => (
                    <button
                      key={key}
                      type="button"
                      className={`ev-btn-small ev-bold-reg flex-grow-1 ${activeTagType === key ? 'ev-btn-yellow-fill-round' : 'ev-btn-lightblue-fill-round'}`}
                      onClick={() => { setActiveTagType(key); setSearch(''); }}
                    >
                      {label}
                      {tempCategory[key].length > 0 && ` (${tempCategory[key].length})`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search — compact; shadow-none removes the focus glow */}
            <div className="position-relative mb-2 flex-shrink-0">
              <input
                type="text"
                className="form-control search-input m-0 py-1 ev-fs-11 shadow-none"
                placeholder={isCountryTab ? 'Search countries...' : 'Search tags...'}
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
              <button
                type="button"
                className="ev-btn-lightblue-fill-round search-bar-button mt-0 top-50 start-0 translate-middle-y py-1 px-2 ev-fs-11"
              >
                <i className="bi bi-search"></i>
              </button>
            </div>

            {/* Select All / Deselect All */}
            <div className="d-flex justify-content-end mb-2 flex-shrink-0">
              <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ev-btn-xsmall"
                onClick={handleSelectAll}>Select All</button>
              <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ev-btn-xsmall ms-2"
                onClick={handleDeselectAll}>Deselect All</button>
            </div>

            {/* Options — height from App.css (.tag-multiselect-dropdown .scrollable-element):
                at least --px-250, and it takes any extra height the box gets. */}
            {isCountryTab ? (
              // One country per row — a plain scannable list, not wrapped pills.
              <div className="scrollable-element pt-1">
                {filteredCountries.length === 0 ? (
                  <span className="ev-xsmall-p-dark">No countries found</span>
                ) : filteredCountries.map(c => {
                  const isSel = tempCountry.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggleCountry(c.id)}
                      className={`ev-select-row border-0 text-start ev-fs-11 ${isSel ? 'selected' : 'bg-transparent'}`}
                    >
                      <i className={`bi ${isSel ? 'bi-check-square-fill' : 'bi-square'} ev-fs-13`}></i>
                      <CountryFlag iso3={c.iso_code} name={c.name} className="country-card__flag" />
                      <span className="ev-select-row-text">{c.name}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="scrollable-element d-flex flex-wrap gap-1 align-items-start align-content-start pt-1">
                {filteredTags.length === 0 ? (
                  <span className="ev-xsmall-p-dark">No tags found</span>
                ) : filteredTags.map(tag => {
                  const isSel = tempCategory[activeTagType].includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      className={`category-badge ev-fs-11 py-1 px-2 ${isSel ? 'ev-bold-med' : 'ev-bold-reg opacity-75'}`}
                      style={TAG_TYPE_STYLE[activeTagType]}
                      onClick={() => toggleTag(tag)}
                    >
                      {isSel && <i className="bi bi-check2 me-1"></i>}
                      {tag}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Footer */}
            <div className="d-flex justify-content-end gap-2 align-items-center mt-2 pt-2 flex-shrink-0">
              <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small"
                onClick={handleClearAll}>Clear All</button>
              <button type="button" className="ev-btn-yellow-fill-round ev-bold-reg"
                onClick={handleApply}>Apply</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Tags display on fabric card ─────────────────────────────────────────────
// category is { indication: [...], intervention: [...] }. A few tags show inline
// as gh-tag-pill pills, coloured by bucket (one colour for Indication, one for
// Intervention — size stays the gh-tag-pill size). Clicking any pill, or
// "+N more", opens a popup listing everything grouped by bucket. The popup
// sizes itself to its content (capped at 70vh) so a handful of tags doesn't
// leave a big empty card underneath them.

const FabricTagsDisplay = ({ category, fabricName }) => {
  const [showPopup, setShowPopup] = useState(false);
  const indicationTags = ((category && category.indication) || []).filter(Boolean);
  const interventionTags = ((category && category.intervention) || []).filter(Boolean);
  // Keep each tag's bucket so its pill can take that bucket's colour.
  const tagList = [
    ...indicationTags.map(tag => ({ tag, type: 'indication' })),
    ...interventionTags.map(tag => ({ tag, type: 'intervention' }))
  ];
  if (tagList.length === 0) return null;

  const MAX_VISIBLE = 3;
  const visibleTags = tagList.slice(0, MAX_VISIBLE);
  const remaining = tagList.length - MAX_VISIBLE;

  const openPopup = (e) => { e.stopPropagation(); setShowPopup(true); };
  const closePopup = () => setShowPopup(false);

  const renderTagGroup = (title, type, tags) => tags.length > 0 && (
    <div className="mb-3 last-child-mb-0">
      <p className="ev-xsmall-p-blue ev-bold-med mb-2">{title}</p>
      <div className="ev-fabric-tags">
        {tags.map(tag => (
          <span key={tag} className="gh-tag-pill" style={TAG_TYPE_STYLE[type]}>{tag}</span>
        ))}
      </div>
    </div>
  );

  return (
    <div className="ev-fabric-tags mb-2">
      {visibleTags.map(({ tag, type }) => (
        <button key={`${type}-${tag}`} type="button"
          className="gh-tag-pill" style={TAG_TYPE_STYLE[type]} onClick={openPopup}>
          {tag}
        </button>
      ))}
      {remaining > 0 && (
        <button type="button" className="gh-tag-pill" onClick={openPopup}>
          +{remaining} more
        </button>
      )}

      {showPopup && ReactDOM.createPortal(
        <div
          className="position-fixed d-flex align-items-center justify-content-center modal fade show d-block bg-dark bg-opacity-50"
          style={{ zIndex: 'var(--num-1000000000)' }}
          onClick={closePopup}
        >
          {/* No fixed vh height here — the card is only ever as tall as its
              tags need, capped at 70vh with its own scroll for a project
              that genuinely has a lot of tags. */}
          <div
            className="ev-card-2 p-4 col-5 d-flex flex-column"
            style={{ maxHeight: '70vh' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center mb-3 flex-shrink-0">
              <p className="ev-med-p-blue mb-0">
                Tags{fabricName ? <span className="ev-small-p-dark ms-2">{fabricName}</span> : null}
              </p>
              <button className="ev-btn-text-only-fill ev-btn-large" onClick={closePopup}>
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
            <div className="scrollable-element" style={{ height: 'auto', minHeight: 0, overflowY: 'auto' }}>
              {renderTagGroup('Indication', 'indication', indicationTags)}
              {renderTagGroup('Intervention', 'intervention', interventionTags)}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const DataFabricList = ({
  fabrics, 
  total, 
  page, 
  setPage, 
  size, 
  setSize,
  search, 
  setSearch, 
  filterType, 
  fabricCategories,      // { indication: [...], intervention: [...] }
  setFilterType,
  countries,              // [{ id, name, iso_code }, ...]
  countryFilter,          // [id, ...]
  setCountryFilter,
  externalCategoryFilter = [],
  categoryFilter = EMPTY_CATEGORY_FILTER,
  setCategoryFilter,
  onRefresh, 
  onEditFabric, 
  onViewFabricDetails,
  onCardClick,
}) => {
  // Delete fabric states - updated for reusable modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fabricToDelete, setFabricToDelete] = useState(null);
  
  // Search input state (separate from the actual search term)
  const [searchInput, setSearchInput] = useState(search);
  const [userOverrideCategory, setUserOverrideCategory] = useState(false);
  const [sortOrder, setSortOrder] = useState('latest');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortRef = useRef(null);
  
  // Sync local category filter when sidebar category changes. The sidebar
  // only knows about a single flat category name, so it's treated as an
  // Indication tag — adjust here if your sidebar categories should map to
  // Intervention (or a mix) instead.
  useEffect(() => {
    if (externalCategoryFilter.length > 0) {
      setCategoryFilter({ indication: externalCategoryFilter, intervention: [] });
      setUserOverrideCategory(false); // Reset override when sidebar changes
    } else {
      // "HTA Dossier Studio" clicked — clear all filters, show all projects
      setCategoryFilter(EMPTY_CATEGORY_FILTER);
      setUserOverrideCategory(false);
    }
  }, [externalCategoryFilter]);
  
  useEffect(() => {
    const handler = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) setIsSortOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const navigate = useNavigate();

  // API base URL
  const API_BASE_URL = '/pceapi/fabrics';

  // HTTP headers with authorization
  const headers = {
    Authorization: GetCookie('access_token')
  };
  
  // Show delete confirmation dialog
  const confirmDelete = (fabric) => {
    setFabricToDelete(fabric);
    setShowDeleteConfirm(true);
  };
  
  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };
  
  // Close success popup
  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    onRefresh();
  };
  
  // Handle fabric deletion
  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    
    if (!fabricToDelete) return;
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(`Deleting project "${fabricToDelete.name}"`);
    
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/delete`,
        { 
          headers,
          data: {
            fabric_id: fabricToDelete.id
          }
        }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        authNavigate('/login', 'iam', navigate);
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to delete project');
        setIsErrorPopupVisible(true);
        return;
      }
      window.dispatchEvent(new Event('category_updated'));
      setPopupSuccessMessage(data.message || 'Project deleted successfully');
      setIsSuccessPopupVisible(true);
      
    } catch (error) {
      console.error('Error deleting project:', error);
      setPopupErrorMessage(error.message || 'Failed to delete project');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setFabricToDelete(null);
    }
  };

  const handleSearch = () => { setSearch(searchInput); setPage(1); };
  const handleSearchKeyPress = (e) => { 
    if (e.key === 'Enter') {
      handleSearch();
    }
  };


  const filteredFabrics = [...fabrics].sort((a, b) => {
    if (sortOrder === 'latest') return new Date(b.created_at) - new Date(a.created_at);
    if (sortOrder === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
    if (sortOrder === 'a-z') return (a.name || '').localeCompare(b.name || '');
    if (sortOrder === 'z-a') return (b.name || '').localeCompare(a.name || '');
    return 0;
  });

  // Pagination uses backend total (already filtered by tags/country on server)
  const totalPages = Math.ceil(total / size);

  // Name lookup for the active-filter chips (country filter stores ids).
  const countryNameById = (id) => (countries.find(c => c.id === id) || {}).name || id;

  const removeCountryFilter = (id) => {
    setCountryFilter(countryFilter.filter(c => c !== id));
    setPage(1);
  };
  const removeTagFilter = (type, tag) => {
    setCategoryFilter({ ...categoryFilter, [type]: categoryFilter[type].filter(t => t !== tag) });
    setPage(1);
  };

  const hasActiveFilters =
    countryFilter.length > 0 || categoryFilter.indication.length > 0 || categoryFilter.intervention.length > 0;

  // Single card renderer — reused by flat render
  const renderFabricCard = (fabric) => (
    <div key={fabric.id} className="col-4 mb-4">
      <div 
        className="ev-message-card1 bms-fabric-card d-block ev-radius-small ev-padding-large h-100 position-relative"
      >
        <div className='pb-3'>
          <div className="row p-0 align-items-center mb-3">
            <div className="col-6 ps-1">
              <p className="ev-message-card3 mb-0">{fabric.thread_count} Data folders</p>
            </div>
            <div className="col-6 d-flex justify-content-end align-items-center">
              <CountryFlag iso3={fabric.country_code} name={fabric.country_name} />
            </div>
          </div>
          <p className="ev-reg-p-blue mb-2 avatar-tooltip ev-bold-med" data-tooltip={fabric.name || 'None'}>
            <TrimContent text={fabric.name} maxLength={30} />
          </p>
          <div className="w-100 mb-2">
            <p className="ev-xsmall-p-dark m-0 mb-1 avatar-tooltip" data-tooltip={fabric.created_by_email}>
              <i className="bi bi-person-fill me-1"></i><b>Owner</b>&nbsp;&nbsp;{fabric.created_by_name || <TrimContent text={fabric.created_by_email} maxLength={25} />}
            </p>
            <p className="ev-xsmall-p-dark m-0 mb-1 avatar-tooltip" data-tooltip={fabric.country_name || 'Not assigned'}>
              <i className="bi bi-globe me-1"></i><b>Country</b>&nbsp;&nbsp;<TrimContent text={fabric.country_name || 'Not assigned'} maxLength={25} />
            </p>
            <p className="ev-xsmall-p-dark m-0">
              <i className="bi bi-clock me-1"></i><b>Updated</b>&nbsp;&nbsp;{new Date(fabric.updated_at || fabric.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
          <hr className="my-2" />
          <p className="ev-xsmall-p-dark mb-0 ev-bold-med">Description</p>
          <p className="ev-xsmall-p-dark mb-2 fst-italic">
            <TrimContent text={fabric.description || 'No description added yet'} maxLength={80} />
          </p>
          <p className="ev-xsmall-p-dark mb-1 ev-bold-med">Tags</p>
          <FabricTagsDisplay category={fabric.category} fabricName={fabric.name} />
        </div>
        <div className="d-flex justify-content-between align-items-center position-absolute bottom-5 start-0 end-0 px-4">
          <button className="ev-btn-blue-fill-round ev-bold-reg"
            onClick={(e) => { e.stopPropagation(); onViewFabricDetails(fabric.id); }}>
            Settings <i className="bi bi-chevron-right"></i>
          </button>
          <button className="ev-btn-blue-fill-round ev-bold-reg"
            onClick={(e) => { e.stopPropagation(); onCardClick && onCardClick(fabric.id); }}>
            Open Project
          </button>
        </div>
      </div>
    </div>
  );
  
  return (
    <div className="ev-card-4 w-100 px-5 py-4">
      {/* Toolbar */}
      <div className="d-flex justify-content-between mb-3 mt-1 row">
        <div className="position-relative w-40 col-5">
          <input
            type="text"
            className="form-control search-input ms-0"
            placeholder="Search by project name or description"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
          />
          <button 
            className="ev-btn-lightblue-fill-round search-bar-button" 
            onClick={handleSearch}
          >
            <i className="bi bi-search"></i>
          </button>
        </div>
        
        <div className="col-7 d-flex justify-content-end align-items-center gap-2">

          <div className="position-relative d-inline-block" ref={sortRef}>
            <button
              type="button"
              className="ev-message-card2 ev-btn-xsmall ev-fs-11"
              onClick={() => setIsSortOpen(o => !o)}
            >
              <i className="bi bi-sort-down me-1"></i>Sort
              <i className={`bi bi-chevron-${isSortOpen ? 'up' : 'down'} ms-1`}></i>
            </button>
            {isSortOpen && (
              <div className="sort-dropdown position-absolute">
                {[
                  { value: 'latest', label: 'Latest to Oldest', icon: 'bi-sort-down' },
                  { value: 'oldest', label: 'Oldest to Latest', icon: 'bi-sort-up' },
                  { value: 'a-z',    label: 'A – Z',            icon: 'bi-sort-alpha-down' },
                  { value: 'z-a',    label: 'Z – A',            icon: 'bi-sort-alpha-up' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`sort-option${sortOrder === opt.value ? ' sort-option-active' : ''}`}
                    onClick={() => { setSortOrder(opt.value); setIsSortOpen(false); }}
                  >
                    <i className={`bi ${opt.icon}`}></i> {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <FiltersDropdown
            countries={countries}
            countryFilter={countryFilter}
            onCountryChange={(val) => { setCountryFilter(val); setPage(1); }}
            categories={fabricCategories}
            categoryFilter={categoryFilter}
            onCategoryChange={(val) => { setCategoryFilter(val); setPage(1); }}
          />

          <button
            className="ev-btn-lightblue-fill-round ev-bold-reg"
            onClick={onRefresh}
          >
            <i className="bi bi-arrow-repeat"></i> Refresh
          </button>
        </div>
      </div>
      
      {/* Active filter chips — countries first, then Indication tags, then Intervention tags */}
      {hasActiveFilters && (
        <div className="d-flex flex-wrap gap-1 mb-2">
          {countryFilter.map(id => (
            <span key={`country-${id}`}
              className={`category-badge ${getPillClass(countryNameById(id))} avatar-tooltip ev-bold-reg ev-fs-11 py-1 px-2`}
              data-tooltip={countryNameById(id)}>
              <i className="bi bi-globe me-1"></i><TrimContent text={countryNameById(id)} maxLength={20} />
              <button type="button" className="border-0 bg-transparent p-0 ms-2 text-reset"
                aria-label={`Remove ${countryNameById(id)} filter`}
                onClick={() => removeCountryFilter(id)}>
                <i className="bi bi-x"></i>
              </button>
            </span>
          ))}
          {TAG_TYPES.map(({ key, label }) => categoryFilter[key].map(tag => (
            <span key={`${key}-${tag}`}
              className="category-badge avatar-tooltip ev-bold-reg ev-fs-11 py-1 px-2"
              style={TAG_TYPE_STYLE[key]}
              data-tooltip={`${label}: ${tag}`}>
              <i className="bi bi-tag me-1"></i><TrimContent text={tag} maxLength={20} />
              <button type="button" className="border-0 bg-transparent p-0 ms-2 text-reset"
                aria-label={`Remove ${tag} filter`}
                onClick={() => removeTagFilter(key, tag)}>
                <i className="bi bi-x"></i>
              </button>
            </span>
          )))}
        </div>
      )}

      {filteredFabrics.length === 0 ? (
        <div className="col-12 text-center my-5">
          <p className="ev-reg-p-blue">No Projects found</p>
          {hasActiveFilters && (
            <p className="col-12 text-center ev-reg-p-blue">
              <i className="bi bi-info-circle"></i> Try removing some filters to see more projects
            </p>
          )}
        </div>
      ) : (
        <div className="row">{filteredFabrics.map(renderFabricCard)}</div>
      )}

      {/* Pagination */}
      {fabrics.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="ev-small-p-dark">
            Showing {filteredFabrics.length} of {total} entries
          </div>
          <div className="d-flex gap-2">
            <button 
              className="ev-btn-light-fill" 
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <i className="bi bi-chevron-left"></i> 
            </button>
            <button className="ev-btn-light-fill" disabled>
              Page {page} of {totalPages || 1}
            </button>
            <button 
              className="ev-btn-light-fill" 
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
          <div>
           <PaginationControls 
              initialSize={size}
              onSizeChange={(newSize) => {
                setSize(newSize);
                setPage(1); // Reset to first page
              }}
            />
          </div>
        </div>
      )}
      
      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
      
      {isSuccessPopupVisible && (
        <SuccessPopup 
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
      
      {/* Reusable Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setFabricToDelete(null);
        }}
        onConfirm={handleDelete}
        objectName="project"
        itemName={fabricToDelete ? fabricToDelete.name : ''}
        bodyContent={
          <div>
            <p className="mb-0">This will permanently delete:</p>
            <ul>
              <li>The project metadata and settings</li>
              <li>All Data folders links within this project</li>
              <li>All feature suites and operational components within this project</li>
            </ul>
            <p className="mb-q">Note: The underlying Data folders and their documents will not be deleted.</p>
            <p className="text-danger mb-0">This action cannot be undone.</p>
          </div>
        }
      />
    </div>
  );
};

export default DataFabricList;